/**
 * @file `MessageComposer` — the subject + body editor in the compose
 *       flow's Delivery + Message tab.
 * @module @labcraft/admin/ui/emails/message-composer
 *
 * Two modes behind a Text | HTML segment. Text is a plain textarea. HTML is a
 * side-by-side Markdown editor + framed live {@link EmailPreview} that renders
 * the exact HTML that will be sent ({@link renderMarkdown}, whose output is
 * inside the server sanitizer's allowlist — so the preview equals the sent
 * email). The preview footer is lane-aware (marketing shows the unsubscribe
 * footer, transactional none). Starter templates are offered in HTML mode.
 */

import { useMemo, useState, type JSX } from 'react'
import { EmailPreview } from './email_preview.tsx'
import { renderMarkdown } from './render_markdown.ts'

/** A placeholder-fill starter template (Campaign only). */
export type ComposerTemplate = {
  id: string
  label: string
  md: string
}

/**
 * Props for {@link MessageComposer}.
 *
 * @property subject - The subject line.
 * @property onSubject - Update the subject.
 * @property body - The body (plaintext in text mode, Markdown in HTML mode).
 * @property onBody - Update the body.
 * @property format - `text` or `html`.
 * @property onFormat - Switch format.
 * @property footer - Whether the HTML preview shows the unsubscribe footer.
 * @property from - Actual send-From for this kind, shown in the preview header.
 * @property templates - Optional starter templates (Campaign only).
 */
export type MessageComposerProps = {
  subject: string
  onSubject: (value: string) => void
  body: string
  onBody: (value: string) => void
  format: 'text' | 'html'
  onFormat: (format: 'text' | 'html') => void
  footer: boolean
  from?: string
  templates?: ReadonlyArray<ComposerTemplate>
}

/** Render the shared subject + body editor. */
export function MessageComposer(props: MessageComposerProps): JSX.Element {
  const { subject, onSubject, body, onBody, format, onFormat, footer, from } =
    props
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const isHtml = format === 'html'
  const html = useMemo(
    () => (isHtml ? renderMarkdown(body) : ''),
    [isHtml, body],
  )

  return (
    <section
      className={
        isHtml ? 'compose-section msg msg--html' : 'compose-section msg'
      }>
      <div className="msg-head">
        <h2 className="compose-h__t">Message</h2>
        <div className="msg-head__tools">
          {props.templates && isHtml && (
            <select
              className="lc-input msg-template"
              defaultValue=""
              aria-label="Start from a template"
              onChange={e => {
                const t = props.templates?.find(x => x.id === e.target.value)
                if (t) onBody(t.md)
                e.target.value = ''
              }}>
              <option value="">Start from…</option>
              {props.templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
          {isHtml && (
            <div className="seg" role="group" aria-label="Preview width">
              {(['desktop', 'mobile'] as const).map(d => (
                <button
                  key={d}
                  type="button"
                  className="seg__btn"
                  aria-pressed={device === d}
                  onClick={() => setDevice(d)}>
                  {d === 'desktop' ? 'Desktop' : 'Mobile'}
                </button>
              ))}
            </div>
          )}
          <div className="seg" role="group" aria-label="Message format">
            {(['text', 'html'] as const).map(f => (
              <button
                key={f}
                type="button"
                className="seg__btn"
                aria-pressed={format === f}
                onClick={() => onFormat(f)}>
                {f === 'text' ? 'Text' : 'HTML'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="field">
        <span className="field-label">
          <span>Subject</span>
          <span className="counter">{subject.length}/200</span>
        </span>
        <input
          className="lc-input"
          type="text"
          maxLength={200}
          placeholder="Subject line"
          value={subject}
          onChange={e => onSubject(e.target.value)}
        />
      </div>

      {!isHtml ? (
        <div className="field">
          <span className="field-label">
            <span>Body</span>
            <span className="counter">plain text · no merge variables</span>
          </span>
          <textarea
            className="lc-input emails-body"
            rows={13}
            maxLength={20_000}
            placeholder="Write your message. Plain text."
            value={body}
            onChange={e => onBody(e.target.value)}
          />
        </div>
      ) : (
        <div className="msg-dual">
          <div className="md-pane">
            <div className="md-pane__label">
              Markdown
              <span className="md-hint">
                # heading · **bold** · [link](https://…) · - list
              </span>
            </div>
            <textarea
              className="lc-input md-editor"
              maxLength={40_000}
              placeholder={
                '# Your headline\n\nWrite in Markdown. **Bold**, ' +
                '[links](https://labcraft.dev), and\n- bullet points\n\n' +
                'render in the preview on the right. Raw HTML is not supported.'
              }
              value={body}
              onChange={e => onBody(e.target.value)}
            />
          </div>
          <div className="md-pane">
            <div className="md-pane__label">
              Preview
              <span className="md-hint">
                {footer ? 'with the unsubscribe footer' : 'no footer (direct)'}
              </span>
            </div>
            <EmailPreview
              from={from}
              subject={subject}
              bodyHtml={html}
              device={device}
              footer={footer}
            />
          </div>
        </div>
      )}
    </section>
  )
}
